<template>
  <BaseModal
    :model-value="open"
    :title="active?.title || t('mergeConflict.title')"
    :subtitle="active?.message || t('mergeConflict.message')"
    size="lg"
    :closable="true"
    @update:model-value="onOpenChange"
  >
    <div v-if="active" class="space-y-4">
      <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
        <p class="font-semibold">{{ t('mergeConflict.serverChangedTitle') }}</p>
        <p class="mt-1">{{ t('mergeConflict.serverChangedBody') }}</p>
      </div>

      <dl class="grid gap-2 text-xs text-gray-500 dark:text-gray-400 sm:grid-cols-2">
        <div v-if="active.clientUpdatedAt">
          <dt class="font-semibold uppercase">{{ t('mergeConflict.yourVersion') }}</dt>
          <dd class="mt-0.5 text-gray-800 dark:text-gray-200">{{ formatInstant(active.clientUpdatedAt) }}</dd>
        </div>
        <div v-if="active.serverUpdatedAt">
          <dt class="font-semibold uppercase">{{ t('mergeConflict.serverVersion') }}</dt>
          <dd class="mt-0.5 text-gray-800 dark:text-gray-200">{{ formatInstant(active.serverUpdatedAt) }}</dd>
        </div>
      </dl>

      <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="grid grid-cols-[1.1fr_1fr_1fr] gap-0 border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
          <div class="px-3 py-2">{{ t('mergeConflict.field') }}</div>
          <div class="px-3 py-2">{{ t('mergeConflict.serverValue') }}</div>
          <div class="px-3 py-2">{{ t('mergeConflict.yourValue') }}</div>
        </div>
        <div
          v-for="field in active.fields"
          :key="field.key"
          class="grid grid-cols-[1.1fr_1fr_1fr] gap-0 border-b border-gray-100 text-sm last:border-b-0 dark:border-gray-800"
          :class="field.conflict ? 'bg-red-50/60 dark:bg-red-950/30' : 'bg-white dark:bg-gray-900'"
        >
          <div class="px-3 py-3">
            <p class="font-medium text-gray-900 dark:text-gray-100">{{ field.label }}</p>
            <p v-if="field.conflict" class="mt-1 text-xs font-semibold text-red-700 dark:text-red-300">
              {{ t('mergeConflict.sameFieldChanged') }}
            </p>
            <p v-else-if="field.serverChanged" class="mt-1 text-xs text-amber-700 dark:text-amber-300">
              {{ t('mergeConflict.serverFieldChanged') }}
            </p>
          </div>
          <div class="min-w-0 whitespace-pre-wrap break-words px-3 py-3 text-gray-700 dark:text-gray-300">
            {{ field.serverValue }}
          </div>
          <div class="min-w-0 whitespace-pre-wrap break-words px-3 py-3 text-gray-700 dark:text-gray-300">
            {{ field.draftValue }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-wrap justify-end gap-2">
        <BaseButton type="button" variant="outline" @click="settle('keepEditing')">
          {{ t('mergeConflict.keepEditing') }}
        </BaseButton>
        <BaseButton type="button" variant="outline" @click="settle('useServer')">
          {{ t('mergeConflict.useServer') }}
        </BaseButton>
        <BaseButton type="button" variant="primary" @click="settle('saveMine')">
          {{ t('mergeConflict.saveMine') }}
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseModal from '@/components/base/BaseModal.vue'
import { useMergeConflictsStore, type MergeConflictAction } from '@/stores/merge-conflicts'

const { t } = useI18n()
const store = useMergeConflictsStore()

const active = computed(() => store.active)
const open = computed(() => active.value != null)

function settle(action: MergeConflictAction): void {
  store.settle(action)
}

function onOpenChange(value: boolean): void {
  if (!value) settle('keepEditing')
}

function formatInstant(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}
</script>
